import { action, computed, makeObservable } from 'mobx'
import AuthStore, { AuthInitialScreen } from 'auth/AuthStore'
import { task } from 'mobx-task'
import SessionStore from 'iam/SessionStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import MemberStore from 'iam/MemberStore'
import { Id, Router } from 'types/definitions'
import WorkspaceSelectorState from './WorkspaceSelector/WorkspaceSelectorState'
import WorkspaceSignupState from './WorkspaceSignupContainer/WorkspaceSignupState'
import Workspace from 'iam/models/Workspace'
import { ignore404 } from 'utils/error-util'
import { AlertFactory, AlertState } from 'alerts/Alert'
import InvitationStore from 'iam/InvitationStore'
import { RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { ParsedQs } from 'qs'
import { find } from 'rxjs/operators'
import WorkspaceSignupStateAdapter from './WorkspaceSignupContainer/WorkspaceSignupStateAdapter'
import { LastWorkspaceStorage } from 'infra/LastWorkspace'
import { localStorage } from 'infra/storage'
import { firstValueFrom } from 'rxjs'
import UserDataStore from 'iam/UserDataStore'
import memoize from 'memoizee'

export default class AuthenticationContainerState {
  workspaceSelectorState: WorkspaceSelectorState
  workspaceSignupState: WorkspaceSignupState
  private jobManagementLastWorkspaceStorage = new LastWorkspaceStorage(
    localStorage,
    'jobManagement',
  )

  private userManagementLastWorkspaceStorage = new LastWorkspaceStorage(
    localStorage,
    'userManagement',
  )

  constructor(
    private sessionStore: SessionStore,
    private workspaceMembershipsStore: WorkspaceMembershipStore,
    private invitationStore: InvitationStore,
    private workspaceStore: WorkspaceStore,
    private memberStore: MemberStore,
    private authStore: AuthStore,
    private userDataStore: UserDataStore,
    private router: Router,
    private newAlert: AlertFactory,
    private lastWorkspaceStorage: LastWorkspaceStorage,
  ) {
    makeObservable(this)
    this.workspaceSelectorState = new WorkspaceSelectorState(
      this.workspaceMembershipsStore,
      this.workspaceStore,
      this.sessionStore,
      this.checkMemberExists,
      this.lastWorkspaceStorage,
    )

    const workspaceSignupStateAdapter = new WorkspaceSignupStateAdapter(
      this.workspaceStore,
      this.sessionStore,
      this.memberStore,
      this.workspaceMembershipsStore,
      this.newAlert,
      this.checkMemberExists,
    )

    this.workspaceSignupState = new WorkspaceSignupState(
      workspaceSignupStateAdapter,
    )
    this.checkAuthentication = memoize(this.checkAuthentication.bind(this), {
      promise: true,
    })
  }

  handleParams = task(async (qs: ParsedQs) => {
    if (this.isAuthenticated) {
      return
    }

    if (
      typeof qs.error_description === 'string' &&
      qs.error_description === 'UNABLE_TO_LINK'
    ) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          'We were unable to link your accounts. Please reach out to support for help.',
        )
        .autoDismiss()
        .show()
    }

    if (
      typeof qs.error_description === 'string' &&
      qs.error_description === 'UNABLE_TO_SIGN_IN'
    ) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Something went wrong. Please reach out to support for help.')
        .autoDismiss()
        .show()
    }

    if (typeof qs.show === 'string' && ['login', 'signUp'].includes(qs.show)) {
      const opts: RedirectLoginOptions = {}

      if (typeof qs.connection === 'string') {
        opts.authorizationParams = {
          ...opts.authorizationParams,
          connection: qs.connection,
        }
      }

      if (typeof qs.login_hint === 'string') {
        opts.authorizationParams = {
          ...opts.authorizationParams,
          login_hint: qs.login_hint,
        }
      }

      await this.authorize(qs.show as AuthInitialScreen, opts)
    }
  })

  /**
   * Activates the store, initializing all the things.
   */
  activate = task(async (inviteToken?: string) => {
    await this.checkAuthentication()

    if (!this.isAuthenticated) {
      return
    }

    // Wait for real user to actually load, this can be cleaner if we refactor it
    // how the isAuthenticated works by instead of checking if the user is set
    // to instead check a bool and then let the current user always be loaded in the
    // background so we don't have to populate the dummy user
    await this.waitForCurrentUser()

    const membershipsPromise =
      this.workspaceMembershipsStore.fetchWorkspaceMemberships()
    const preSelectWorkspace = inviteToken
      ? await this.handleInvitation(inviteToken)
      : undefined
    if (!preSelectWorkspace) {
      const memberships = await membershipsPromise
      if (memberships.length === 0) {
        this.workspaceSignupState.toggleConfigurerOpen()
      }
    }

    await membershipsPromise

    await this.userDataStore.fetchByPublicId(
      Id.assert(this.sessionStore.user.publicId),
    )
    await this.workspaceSelectorState.activate(preSelectWorkspace)
    await this.workspaceSignupState.activate()
  })

  /**
   * Checks to ensure we are authenticated.
   */
  async checkAuthentication() {
    await this.authStore
      .checkAuthentication()
      .catch((err) =>
        this.newAlert().withState(AlertState.Error).saying(err.message).show(),
      )
  }

  deactivate() {
    //
  }

  @computed
  get isAuthenticated() {
    return this.sessionStore.isAuthenticated
  }

  @action
  authorize(initialScreen: AuthInitialScreen, opts?: RedirectLoginOptions) {
    return this.authStore._showDialogAndAuthorize(initialScreen, opts)
  }

  handleInvitation = async (
    inviteToken: string,
  ): Promise<Workspace | undefined> => {
    this.router.replace('/')
    const invitation = await this.invitationStore
      .get(inviteToken)
      .catch(ignore404)
    if (invitation && invitation.status === 'PENDING') {
      const workspace = await this.workspaceStore.fetchWorkspace(
        invitation.workspace_id,
      )
      if (workspace) {
        const member = await this.invitationStore
          .accept({
            token: invitation.token,
          })
          .then((data) => this.memberStore.members.set(data))
        this.workspaceMembershipsStore.addMembership({ workspace, member })
        return workspace
      }
    }
    return undefined
  }

  checkMemberExists = async (workspace: Workspace) => {
    const member = await this.memberStore
      .fetch({
        workspace_id: Id.assert(workspace.id),
        user_id: Id.assert(this.sessionStore.user.id),
        user_public_id: this.sessionStore.user.publicId,
      })
      .catch(ignore404)
    if (
      // If the member doesn't exist within the workspace
      !member ||
      !member.internalId ||
      // .. or the member exists but we dont have names and emails
      member.givenName.length === 0 ||
      member.familyName.length === 0 ||
      member.email.length === 0
    ) {
      this.workspaceSignupState.maybeSyncMember(workspace)
      return false
    }

    this.setLastSelectedWorkspace(
      Id.assert(workspace.id),
      Id.assert(this.sessionStore.user.publicId),
    )

    return true
  }

  setLastSelectedWorkspace(workspaceId: string, userId: string) {
    // Save to global storage
    this.lastWorkspaceStorage.save(workspaceId, userId)

    // Propagate to Job Management storage
    // Job Management expects JSON stringified array of workspace IDs
    this.jobManagementLastWorkspaceStorage.save(
      JSON.stringify([workspaceId]),
      userId,
    )

    // Propagate to User Management storage
    // User Management expects plain workspace ID string
    this.userManagementLastWorkspaceStorage.save(workspaceId, userId)
  }

  async waitForCurrentUser() {
    await firstValueFrom(
      this.sessionStore.currentUser$.pipe(find((u) => u.isFullyLoaded)),
      { defaultValue: null },
    )
  }
}
